import axios, { type AxiosResponse } from 'axios'
import BaseService from '../BaseService'

const GRAPHQL_API = `${import.meta.env.VITE_HOTEL_SEARCH_GRAPHQL_API_URL}`

// ==================== GraphQL Queries ====================

const getTranslations = `query Translations($snippets: [TranslationsSnippetInput!]!) {
  getTranslations(snippets: $snippets) {
    translations {
      translation
      snippet
    }
  }
}`

const getAccommodationDetails = `query ($input: AccommodationDetailsInput!) {
  getAccommodationDetails(input: $input) {
    accommodationDetails {
      nsid {
        id
        ns
      }
      translatedName {
        value
      }
        businessStudio {
        subscriptionStatus,
        activeOffer {
          title {
            snippet
            translation {
              value
            }
          }
          description {
            snippet
            translation {
              value
            }
          }
          expirationDate
        }
      }
    }
  }
}`

// ==================== Type Definitions ====================

/** Hotel offer template status */
export type HotelOfferTemplateStatus = 'COMING_SOON' | 'ACTIVE' | 'DELETED'

/** Hotel offer template type */
export type HotelOfferTemplateType = 'FREE' | 'DISCOUNT'

/** Hotel offer history action */
export type HotelOfferHistoryAction = 'CREATED' | 'UPDATED' | 'DELETED' | 'AUTO_DELETED'

/** Hotel offer template */
export interface HotelOfferTemplate {
  name: string
  category: string
  status: HotelOfferTemplateStatus
  type: HotelOfferTemplateType
  displayOrder: number
}

/** Hotel offer (active or from API response) */
export interface HotelOffer {
  id: number
  template: HotelOfferTemplate
  accommodationId: number
  publishingDate: string
  expirationDate: string | null
  benefitValue: number | null
  createdAt: string
  createdBy: string
  updatedAt: string | null
  updatedBy: string | null
  [key: string]: unknown
}

/** Hotel offer history entry */
export interface HotelOfferHistory {
  id: number
  action: HotelOfferHistoryAction
  hotelOfferId: number | null
  template: HotelOfferTemplate
  accommodationId: number
  publishingDate: string
  expirationDate: string | null
  benefitValue: number | null
  createdAt: string
  createdBy: string
  updatedAt: string | null
  updatedBy: string | null
}

/** Create hotel offer request body */
export interface CreateHotelOfferBody {
  templateName: string
  expirationDate?: string
  benefitValue?: number
}

/** Update hotel offer request body */
export interface UpdateHotelOfferBody {
  templateName?: string
  expirationDate?: string
  benefitValue?: number
}

/** Offer feedback body */
export interface OfferFeedbackBody {
  feedback: string
  [key: string]: any
}

/** Translation snippet input */
export interface TranslationSnippet {
  snippet: string
}

/** Translation result */
export interface Translation {
  translation: string
  snippet: string
}

/** Translations response from GraphQL */
export interface TranslationsResponse {
  getTranslations: {
    translations: Translation[]
  }
}

/** NSID structure */
export interface NSID {
  id: number
  ns: number
}

/** Translated value */
export interface TranslatedValue {
  value: string
}

/** Offer text (title or description) */
export interface OfferText {
  snippet: string
  translation: TranslatedValue
}

/** Active offer from GraphQL */
export interface ActiveOffer {
  title: OfferText
  description: OfferText
  expirationDate: string
}

/** Business studio info */
export interface BusinessStudioInfo {
  subscriptionStatus: string
  activeOffer: ActiveOffer | null
}

/** Accommodation details */
export interface AccommodationDetail {
  nsid: NSID
  translatedName: TranslatedValue
  businessStudio: BusinessStudioInfo
}

/** Accommodation details response from GraphQL */
export interface AccommodationDetailsResponse {
  getAccommodationDetails: {
    accommodationDetails: AccommodationDetail[]
  }
}

/** Request interfaces */
export interface AccommodationRequest {
  accommodationId: string
}

export interface CreateOfferRequest {
  accommodationId: string
  replaceOldOffer: boolean
  body: CreateHotelOfferBody
}

export interface UpdateOfferRequest {
  accommodationId: string
  body: UpdateHotelOfferBody
}

export interface DeleteOfferRequest {
  accommodationId: string
  feedback: string
}

export interface FetchTranslationsRequest {
  snippets: TranslationSnippet[]
  languageCode: string
}

export interface FetchAccommodationOfferRequest {
  accommodationId: string
  languageCode: string
}

/** Offer category grouping templates */
export interface OfferCategory {
  name: string
  templates: HotelOfferTemplate[]
}

/** Response from fetchTemplates endpoint */
export interface FetchTemplatesResponse {
  categories: OfferCategory[]
  templates: HotelOfferTemplate[]
}

/** Response from getActiveOffer / updateOffer endpoints */
export interface ActiveOfferResponse {
  offers: HotelOffer[]
}

// ==================== Service Class ====================

class HotelOffersService extends BaseService {
  constructor() {
    super({
      baseUrl: import.meta.env.VITE_PREMIUM_API_URL,
    })
  }

  /**
   * Fetch hotel offer templates
   */
  fetchTemplates(): Promise<AxiosResponse<FetchTemplatesResponse>> {
    if (!this.studioToken) {
      throw new Error('Error in GET /plus/offer/templates: No studioToken provided')
    }

    return axios({
      url: `${this.baseUrl}/plus/offer/templates`,
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Studio-Authorization': this.studioToken,
        ...this.getTrafficAttributionHeaders(),
      },
    })
  }

  /**
   * Send feedback about offers feature
   */
  sendFeedback(body: OfferFeedbackBody): Promise<AxiosResponse> {
    if (!this.studioToken) {
      throw new Error('Error in POST /plus/offer/feedback: No studioToken provided')
    }

    return axios({
      url: `${this.baseUrl}/plus/offer/feedback`,
      method: 'POST',
      data: body,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Studio-Authorization': this.studioToken,
        ...this.getTrafficAttributionHeaders(),
      },
    })
  }

  /**
   * Create a new hotel offer
   */
  createOffer({ 
    accommodationId, 
    replaceOldOffer, 
    body 
  }: CreateOfferRequest): Promise<AxiosResponse<HotelOffer>> {
    if (!this.studioToken) {
      throw new Error(`Error in POST /plus/${accommodationId}/offer: No studioToken provided`)
    }

    return axios({
      url: `${this.baseUrl}/plus/${accommodationId}/offer?replaceOldOffer=${replaceOldOffer}`,
      method: 'POST',
      data: body,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Studio-Authorization': this.studioToken,
        ...this.getTrafficAttributionHeaders(),
      },
    })
  }

  /**
   * Get active hotel offer
   */
  getActiveOffer({ accommodationId }: AccommodationRequest): Promise<AxiosResponse<ActiveOfferResponse>> {
    if (!this.studioToken) {
      throw new Error(`Error in GET /plus/${accommodationId}/offer: No studioToken provided`)
    }

    return axios({
      url: `${this.baseUrl}/plus/${accommodationId}/offer`,
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Studio-Authorization': this.studioToken,
        ...this.getTrafficAttributionHeaders(),
      },
    })
  }

  /**
   * Get past offers history
   */
  getPastOffers({ accommodationId }: AccommodationRequest): Promise<AxiosResponse<HotelOfferHistory[]>> {
    if (!this.studioToken) {
      throw new Error(`Error in GET /plus/${accommodationId}/offer/history: No studioToken provided`)
    }

    return axios({
      url: `${this.baseUrl}/plus/${accommodationId}/offer/history`,
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Studio-Authorization': this.studioToken,
        ...this.getTrafficAttributionHeaders(),
      },
    })
  }

  /**
   * Delete active hotel offer
   */
  deleteOffer({ accommodationId, feedback }: DeleteOfferRequest): Promise<AxiosResponse> {
    if (!this.studioToken) {
      throw new Error(`Error in DELETE /plus/${accommodationId}/offer: No studioToken provided`)
    }

    return axios({
      url: `${this.baseUrl}/plus/${accommodationId}/offer?feedback=${feedback}`,
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Studio-Authorization': this.studioToken,
        ...this.getTrafficAttributionHeaders(),
      },
    })
  }

  /**
   * Update existing hotel offer
   */
  updateOffer({ accommodationId, body }: UpdateOfferRequest): Promise<AxiosResponse<HotelOffer>> {
    if (!this.studioToken) {
      throw new Error(`Error in PUT /plus/${accommodationId}/offer: No studioToken provided`)
    }

    return axios({
      url: `${this.baseUrl}/plus/${accommodationId}/offer`,
      method: 'PUT',
      data: body,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Studio-Authorization': this.studioToken,
        ...this.getTrafficAttributionHeaders(),
      },
    })
  }

  /**
   * Fetch offer translations from GraphQL
   */
  fetchOffersTranslations({
    snippets,
    languageCode
  }: FetchTranslationsRequest): Promise<AxiosResponse<{ data: TranslationsResponse }>> {
    return axios({
      url: `${GRAPHQL_API}`,
      method: 'POST',
      data: {
        query: getTranslations,
        variables: {
          snippets: snippets,
        },
      },
      headers: {
        Accept: 'application/json',
        'x-trv-language': languageCode,
        'Content-Type': 'application/json',
        'X-Studio-Authorization': this.studioToken,
        'Apollographql-Client-Name': 'trivago-business-studio',
        'Apollographql-Client-Version': '1.0.0',
      },
    })
  }

  /**
   * Fetch accommodation offer from GraphQL
   */
  fetchAccommodationOffer({
    accommodationId,
    languageCode
  }: FetchAccommodationOfferRequest): Promise<AxiosResponse<{ data: AccommodationDetailsResponse }>> {
    return axios({
      url: `${GRAPHQL_API}`,
      method: 'POST',
      data: {
        query: getAccommodationDetails,
        variables: {
          input: {
            nsids: [
              {
                id: accommodationId,
                ns: 100,
              },
            ],
          },
        },
      },
      headers: {
        Accept: 'application/json',
        'x-trv-language': languageCode,
        'Content-Type': 'application/json',
        'X-Studio-Authorization': this.studioToken,
        'Apollographql-Client-Name': 'trivago-business-studio',
        'Apollographql-Client-Version': '1.0.0',
      },
    })
  }
}

export default new HotelOffersService()